export type Relationship = 'Immediate Family' | 'Extended Family' | 'Friends' | 'Coworkers' | 'Other'

export type PersonUiMeta = {
  relationship: Relationship
  relationLabel: string
  avatarUrl: string | null
}

export type AvatarCategory = 'adultFemale' | 'adultMale' | 'girl' | 'boy' | 'neutral'

export const PEOPLE_UI_META_KEY = 'timethread-people-ui-meta-v1'

const avatarPools: Record<AvatarCategory, string[]> = {
  adultFemale: [
    'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1719037108848-685e9e599827?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=320&q=80',
  ],
  adultMale: [
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1696389932580-15c246d0662b?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=320&q=80',
  ],
  girl: [
    'https://images.unsplash.com/photo-1497881807663-38b9a95b7192?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1571240571053-3ddc83cdb289?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1597586124394-fbd6ef244026?auto=format&fit=crop&w=320&q=80',
  ],
  boy: [
    'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1566513783362-fd923a368d6b?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1503919005314-30d93d07d823?auto=format&fit=crop&w=320&q=80',
  ],
  neutral: [
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=320&q=80',
    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=320&q=80',
  ],
}

export function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

function inferAvatarCategory(
  name: string,
  notes: string | null,
  relationLabel: string,
  relationship: Relationship,
): AvatarCategory {
  const source = `${name} ${notes || ''} ${relationLabel}`.toLowerCase()
  const hasFemale = /\b(mom|mother|wife|daughter|sister|grandma|grandmother|aunt)\b/.test(source)
  const hasMale = /\b(dad|father|husband|son|brother|grandpa|grandfather|uncle)\b/.test(source)
  const isChild =
    relationship === 'Immediate Family' &&
    /\b(child|kid|son|daughter|oldest|youngest)\b/.test(source)

  if (isChild && hasFemale) return 'girl'
  if (isChild && hasMale) return 'boy'
  if (hasFemale) return 'adultFemale'
  if (hasMale) return 'adultMale'
  return 'neutral'
}

export function pickAvatarForPerson(
  name: string,
  notes: string | null,
  relationLabel: string,
  relationship: Relationship,
  avatarSequenceByCategory?: Map<AvatarCategory, number>,
): string | null {
  const source = `${name} ${notes || ''} ${relationLabel}`.toLowerCase()
  if (/\b(no profile photo|no photo)\b/.test(source)) {
    return null
  }

  const category = inferAvatarCategory(name, notes, relationLabel, relationship)
  const pool = avatarPools[category]
  const nextIndex = avatarSequenceByCategory?.get(category) ?? 0
  if (avatarSequenceByCategory) {
    avatarSequenceByCategory.set(category, nextIndex + 1)
  }
  return pool[nextIndex % pool.length]
}

export function reserveAvatarSlotForPerson(
  name: string,
  notes: string | null,
  relationLabel: string,
  relationship: Relationship,
  avatarSequenceByCategory: Map<AvatarCategory, number>,
) {
  const category = inferAvatarCategory(name, notes, relationLabel, relationship)
  const nextIndex = avatarSequenceByCategory.get(category) ?? 0
  avatarSequenceByCategory.set(category, nextIndex + 1)
}

export function isLegacyAvatarUrl(avatarUrl: string | null) {
  return !!avatarUrl && avatarUrl.includes('testingbot.com/free-online-tools/random-avatar')
}

export function inferRelationship(name: string, notes: string | null): Relationship {
  const source = `${name} ${notes || ''}`.toLowerCase()
  if (/(dad|mom|wife|husband|son|daughter|child|brother|sister)/.test(source)) {
    return 'Immediate Family'
  }
  if (/(grandma|grandpa|grandmother|grandfather|aunt|uncle|cousin|niece|nephew)/.test(source)) {
    return 'Extended Family'
  }
  if (/(coworker|colleague|office|manager|work)/.test(source)) {
    return 'Coworkers'
  }
  if (/(friend|buddy|teammate|neighbor)/.test(source)) {
    return 'Friends'
  }
  return 'Other'
}

export function inferRelationLabel(
  name: string,
  notes: string | null,
  relationship: Relationship,
): string {
  const source = `${name} ${notes || ''}`.toLowerCase()
  if (/\bdad\b|\bfather\b/.test(source)) return 'Dad'
  if (/\bmom\b|\bmother\b/.test(source)) return 'Mom'
  if (/\bhusband\b/.test(source)) return 'Husband'
  if (/\bwife\b/.test(source)) return 'Wife'
  if (/\bgrandpa\b|\bgrandfather\b/.test(source)) return 'Grandpa'
  if (/\bgrandma\b|\bgrandmother\b/.test(source)) return 'Grandma'
  if (/\bson\b/.test(source)) return 'Son'
  if (/\bdaughter\b/.test(source)) return 'Daughter'
  if (/\bchild\b|\bkid\b/.test(source)) return 'Child'
  if (/\bfriend\b|\bbuddy\b/.test(source)) return 'Friend'
  if (/\bcoworker\b|\bcolleague\b/.test(source)) return 'Coworker'
  if (relationship === 'Immediate Family') return 'Family'
  if (relationship === 'Extended Family') return 'Relative'
  if (relationship === 'Friends') return 'Friend'
  if (relationship === 'Coworkers') return 'Coworker'
  return 'Other'
}

export function loadPeopleUiMetaFromStorage(): Record<string, PersonUiMeta> {
  const raw = localStorage.getItem(PEOPLE_UI_META_KEY)
  if (!raw) return {}

  try {
    const parsed = JSON.parse(raw) as Record<string, PersonUiMeta>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    console.error('Failed to parse people UI metadata:', error)
    return {}
  }
}

export function savePeopleUiMetaToStorage(peopleUiMeta: Record<string, PersonUiMeta>) {
  localStorage.setItem(PEOPLE_UI_META_KEY, JSON.stringify(peopleUiMeta))
}
